import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

/**
 * Public EPI certificate verification page (EPI P3). The certificate QR encodes
 * /verify/:eventId/:certId — this page calls the PUBLIC `verifyCertificate` query on
 * api-reception (a DIFFERENT endpoint than www's default api-reg Apollo client), so we fetch
 * it directly. No auth; the resolver returns only safe columns and null for unknown/revoked.
 * Contract: Reception EPI_VERIFY_CONTRACT.md. Mirrors reception-v2's own verify page.
 */

const RECEPTION_GRAPHQL =
  process.env.REACT_APP_RECEPTION_API_URL ||
  "https://api-reception.eventegration.com/graphql";

const QUERY = `
  query VerifyCertificate($eventId: String!, $certId: String!) {
    verifyCertificate(eventId: $eventId, certId: $certId) {
      certId
      eventName
      eps
      dcs
      rating
      coverageModules
      totalModules
      summary
      issued
      revoked
    }
  }
`;

const RATING_GRADIENT = {
  Diamond: "linear-gradient(135deg,#a5f3fc,#22d3ee,#0891b2)",
  Platinum: "linear-gradient(135deg,#e5e7eb,#9ca3af,#6b7280)",
  Gold: "linear-gradient(135deg,#fde68a,#f59e0b,#b45309)",
  Silver: "linear-gradient(135deg,#f1f5f9,#cbd5e1,#94a3b8)",
  Bronze: "linear-gradient(135deg,#fed7aa,#ea9a5b,#b45309)",
};

export default function CertificateVerifyPage() {
  const { eventId, certId } = useParams();
  const [state, setState] = useState({ loading: true, cert: null, error: false });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(RECEPTION_GRAPHQL, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ query: QUERY, variables: { eventId, certId } }),
        });
        const json = await res.json();
        const cert = json && json.data ? json.data.verifyCertificate : null;
        if (alive) setState({ loading: false, cert, error: false });
      } catch (e) {
        if (alive) setState({ loading: false, cert: null, error: true });
      }
    })();
    return () => {
      alive = false;
    };
  }, [eventId, certId]);

  if (state.loading) return <Center>Verifying certificate…</Center>;

  const cert = state.cert;
  const invalid = state.error || !cert || cert.revoked;

  if (invalid) {
    return (
      <Shell>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 48, color: "#ef4444" }}>✕</div>
          <h1 style={{ fontSize: 22, margin: "8px 0 4px" }}>
            {cert && cert.revoked ? "Certificate revoked" : "Not a valid certificate"}
          </h1>
          <p style={{ color: "#94a3b8", fontSize: 14 }}>
            {cert && cert.revoked
              ? "This certificate has been revoked by the organiser."
              : "We couldn't verify this certificate ID. Check the code and try again."}
          </p>
          <p style={{ color: "#64748b", fontSize: 12, marginTop: 12, fontFamily: "monospace" }}>{certId}</p>
        </div>
      </Shell>
    );
  }

  const summary = safeParse(cert.summary, {});
  const grad = RATING_GRADIENT[cert.rating] || "linear-gradient(135deg,#cbd5e1,#94a3b8)";

  return (
    <Shell>
      <div style={{ textAlign: "center" }}>
        <div style={{ color: "#10b981", fontSize: 13, fontWeight: 600, letterSpacing: 1 }}>✓ VERIFIED CERTIFICATE</div>
        <div
          style={{
            width: 120, height: 120, borderRadius: "50%", margin: "16px auto",
            background: grad, display: "grid", placeItems: "center",
            boxShadow: "0 8px 30px rgba(0,0,0,.35)",
          }}
        >
          <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 15, textAlign: "center", padding: 8 }}>{cert.rating}</span>
        </div>
        <h1 style={{ fontSize: 22, margin: "4px 0" }}>{cert.eventName}</h1>
        <p style={{ color: "#94a3b8", fontSize: 13 }}>Event Performance Index</p>

        <div style={{ display: "flex", justifyContent: "center", gap: 28, margin: "20px 0" }}>
          <Stat label="EPS" value={cert.eps} />
          <Stat label="Data confidence" value={cert.dcs + "%"} />
          <Stat label="Modules" value={cert.coverageModules + "/" + cert.totalModules} />
        </div>

        {summary && (summary.attendees != null || summary.exhibitors != null || summary.feedbackScore != null) && (
          <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 16, fontSize: 13, color: "#cbd5e1" }}>
            {summary.attendees != null && <span>{fmt(summary.attendees)} attendees</span>}
            {summary.exhibitors != null && <span>{fmt(summary.exhibitors)} exhibitors</span>}
            {summary.feedbackScore != null && <span>Feedback {summary.feedbackScore}</span>}
          </div>
        )}

        <hr style={{ border: 0, borderTop: "1px solid #1e293b", margin: "20px 0" }} />
        <p style={{ fontFamily: "monospace", fontSize: 13 }}>{cert.certId}</p>
        <p style={{ color: "#64748b", fontSize: 12 }}>Issued {formatDate(cert.issued)}</p>
      </div>
    </Shell>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 30, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#94a3b8" }}>{label}</div>
    </div>
  );
}
function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "#0f172a", color: "#fff", display: "grid", placeItems: "center", padding: 20 }}>
      <div style={{ maxWidth: 460, width: "100%", background: "#1e293b", borderRadius: 16, padding: 28 }}>{children}</div>
    </div>
  );
}
function Center({ children }) {
  return <div style={{ minHeight: "100vh", background: "#0f172a", color: "#94a3b8", display: "grid", placeItems: "center" }}>{children}</div>;
}
function safeParse(s, fb) {
  if (!s) return fb;
  if (typeof s === "object") return s;
  try {
    return JSON.parse(s);
  } catch {
    return fb;
  }
}
function fmt(n) {
  return typeof n === "number" ? n.toLocaleString() : n;
}
function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}
